import React, { Component } from 'react';
import { hot } from 'react-hot-loader';
import { addEvent, removeEvent, addClass, removeClass } from '../../utils/dom';
import s from './Slider.scss';

class Slider extends Component {
	static getDragX(e) {
		return e.touches[0].pageX;
	}

	static getIndex(infinite, childrenLength, index, nextIndex = false) {
		if (nextIndex === false) index += 1;
		else index = nextIndex;

		if (index >= childrenLength + infinite) {
			return 0 + infinite;
		}
		if (index < infinite) {
			return childrenLength + infinite - 1;
		}
		return index;
	}

	constructor(props) {
		super(props);

		this.state = {
			dragStart: 0,
			dragStartTime: new Date(),
			partialIndex: 0,
			index: (props.startIndex + props.infinite) || (0 + props.infinite),
			lastIndex: (props.startIndex + props.infinite) || (0 + props.infinite),
			transition: false,
			disabled: props.children.length === 1,
		};

		this.slider = React.createRef();
		this.sliderInner = React.createRef();
	}

	componentDidMount() {
		const { screenMaxWidthDisable, screenMinWidthDisable, children, autoplay, transitionType } = this.props;
		const { index } = this.state;

		const autoplayFunction = () => {
			// Use this.state.index to get the latest updated index
			const nextIndex = Slider.getIndex(this.props.infinite, this.props.children.length, this.state.index);
			this.goToSlide(nextIndex);
		};

		if (autoplay && children.length > 1) {
			this.autoplayPointer = window.setInterval(autoplayFunction, autoplay);
		}

		const elementsInitiated = () => {
			if (!this.sliderInner.current) {
				setTimeout(() => {
					elementsInitiated();
				}, 50);
			}

			if (this.sliderInner.current?.children?.[0]?.children?.[index]) addClass(this.sliderInner.current.children[0].children[index], 'u_selected');
		};

		if (transitionType === 'fade') elementsInitiated();

		if ((screenMaxWidthDisable !== false || screenMinWidthDisable !== false) && children.length > 1) {
			let isDisabled = false;

			const resizeEvent = () => {
				const width = window.innerWidth;
				if (
					children.length <= 1
					|| (screenMaxWidthDisable !== false && !isDisabled && width > screenMaxWidthDisable)
					|| (screenMinWidthDisable !== false && !isDisabled && width < screenMinWidthDisable)
				) {
					// Only one child or less || Screen is over max width || Screen is under min width -> disable
					isDisabled = true;

					if (autoplay) window.clearInterval(this.autoplayPointer);
					this.setState({
						disabled: true,
					});

					removeEvent(document.body, 'touchmove.slider', { passive: false });
				} else if (
					(screenMaxWidthDisable !== false && isDisabled && width <= screenMaxWidthDisable)
					|| (screenMinWidthDisable !== false && isDisabled && width >= screenMinWidthDisable)
				) {
					isDisabled = false;

					if (autoplay) this.autoplayPointer = window.setInterval(autoplayFunction, autoplay);
					this.setState({
						disabled: false,
					});

					removeEvent(document.body, 'touchmove.slider', { passive: false });
					addEvent(document.body, 'touchmove.slider', (e) => {
						if (this.sliderInner.current && this.sliderInner.current.contains(e.target)) {
							if (Math.abs(e.touches[0].pageX - this.state.dragStart) > 20) {
								e.preventDefault();
							}
						}
					}, { passive: false });
				}
			};

			addEvent(window, 'resize.slider', () => {
				window.requestAnimationFrame(resizeEvent);
			}, { passive: false });

			resizeEvent();
		}
	}

	componentDidUpdate(prevProps) {
		// Restart location
		if (prevProps.location && prevProps.location !== this.props.location) this.goToSlide((this.props.infinite + 0), false, false);
	}

	componentWillUnmount() {
		removeEvent(document.body, 'touchmove.slider', { passive: false });
		if (this.props.autoplay) window.clearInterval(this.autoplayPointer);
		if ((this.props.screenMaxWidthDisable !== false || this.props.screenMinWidthDisable !== false) && this.props.children.length > 1) {
			removeEvent(window, 'resize.slider', { passive: false });
		}
	}

	handleDragStart = (e) => {
		const x = Slider.getDragX(e);

		if (this.props.autoplay) window.clearInterval(this.autoplayPointer);

		this.setState({
			dragStart: x,
			dragStartTime: new Date(),
			transition: false,
			slideWidth: this.slider.current.offsetWidth,
		});
	}

	handleDragMove = (e) => {
		const { dragStart, lastIndex, slideWidth } = this.state;

		const x = Slider.getDragX(e);
		const offset = dragStart - x;
		const percentageOffset = offset / slideWidth;
		const partialIndex = lastIndex + percentageOffset;
		const SCROLL_OFFSET_TO_STOP_SCROLL = 30;

		// Stop scrolling if you slide more than 30 pixels
		if (Math.abs(offset) > SCROLL_OFFSET_TO_STOP_SCROLL) {
			e.stopPropagation();
			e.preventDefault();
		}

		this.setState({
			partialIndex,
		});
	}

	handleDragEnd = () => {
		const { dragStartTime, partialIndex, index, lastIndex } = this.state;

		const timeElapsed = new Date().getTime() - dragStartTime.getTime();
		const offset = index - partialIndex;
		const velocity = Math.round((offset / timeElapsed) * 10000);

		let newIndex = Math.round(partialIndex);

		if (Math.abs(velocity) > 5) {
			newIndex = velocity < 0 ? lastIndex + 1 : lastIndex - 1;
		}

		this.setState({
			dragStart: 0,
			index: newIndex,
			lastIndex: index,
			transition: true,
		});
	}

	handleTransitionEnd = () => {
		const { infinite, children } = this.props;
		const { index } = this.state;
		const newIndex = Slider.getIndex(infinite, children.length, false, index);

		if (newIndex !== index) {
			this.setState({
				index: newIndex,
				transition: false,
			});
		}
	}

	handleClick = (e, dir) => {
		e.preventDefault();
		const { lastIndex } = this.state;

		this.goToSlide(lastIndex + dir, e);
	}

	goToSlide = (nextIndex, e, transition = true) => {
		const { infinite, children, transitionType } = this.props;
		const { index } = this.state;

		if (e) {
			e.preventDefault();
			e.stopPropagation();

			if (this.props.autoplay) window.clearInterval(this.autoplayPointer);
		}

		nextIndex = Slider.getIndex(infinite, children.length, false, nextIndex);

		if (transitionType === 'fade') {
			if (this.sliderInner.current?.children?.[0]?.children?.[index]) removeClass(this.sliderInner.current.children[0].children[index], 'u_selected');
			if (this.sliderInner.current?.children?.[0]?.children?.[nextIndex]) addClass(this.sliderInner.current.children[0].children[nextIndex], 'u_selected');
		}

		this.setState({
			index: nextIndex,
			lastIndex: index,
			transition,
		});
	}

	createThumbs(children) {
		return React.Children.map(children, (child) => {
			if (!React.isValidElement(child)) return child;
			const childProps = {};
			if (child.type === 'img') childProps.src = child.props.src.replace('-zoom', '-thumb');
			childProps.children = this.createThumbs(child.props.children);
			return React.cloneElement(child, childProps);
		});
	}

	renderNav() {
		const { infinite, children } = this.props;
		const { index } = this.state;

		return (
			<div className={s.slider_nav}>
				{
					children.map((slide, i) => (
						<button type="button" className={[s.slider_navButton, (i === (index - infinite) ? s.slider_navButton__active : null)].join(' ')} key={`pagination-${i}`} onClick={(e) => this.goToSlide(i, e)} aria-labelledby={`Slider pagination ${i}`} />
					))
				}
			</div>
		);
	}

	renderArrows() {
		const { children, infinite } = this.props;
		const { index } = this.state;

		return (
			<div className={s.slider_arrows}>
				<button type="button" className={[s.slider_arrow, s.slider_arrow__left, (index === 0 + infinite ? s.slider_arrow__first : null)].join(' ')} onClick={(e) => this.goToSlide(index - 1, e)} aria-labelledby="Slider arrow left" />
				<button type="button" className={[s.slider_arrow, s.slider_arrow__right, (children.length === index - infinite ? s.slider_arrow__last : null)].join(' ')} onClick={(e) => this.goToSlide(index + 1, e)} aria-labelledby="Slider arrow right" />
			</div>
		);
	}

	renderThumbs() {
		const { infinite, children } = this.props;
		const { index } = this.state;
		const thumbChildren = this.createThumbs(children);

		if (thumbChildren.length === 0) return null;

		return (
			<div className={[s.slider_thumbs, 'u_slideThumbs'].join(' ')}>
				<div className={s.slider_thumbs_inner}>
					{
						thumbChildren.map((child, i) => <a key={`${child.key}Thumb`} className={[s.slider_thumbs_inner_thumb, (i === (index - infinite) ? s.slider_thumbs_inner_thumb__selected : null)].join(' ')} href="#link" onClick={(e) => this.goToSlide(i, e)}>{ child }</a>)
					}
				</div>
			</div>
		);
	}

	render() {
		const { children, showArrows, showNav, adaption, infinite, thumbs, interactionTypes, transitionType } = this.props;
		const { index, transition, disabled } = this.state;
		const numberSlides = !disabled ? children.length + (infinite * 2) : children.length;

		const slidesStyles = {};
		if (!disabled && transitionType !== 'fade') {
			slidesStyles.width = `${100 * numberSlides}%`;
			slidesStyles.transform = `translateX(${-1 * index * (100 / numberSlides)}%)`;
		}

		const slidesClasses = [s.slider_slides, 'u_slideMain'];
		if (transition) slidesClasses.push(s.slider_slides__transition);

		const sliderClasses = [s.slider];
		if (adaption !== false) sliderClasses.push(s[`slider__${adaption}`]);
		if (transitionType === 'fade') sliderClasses.push(s.slider__fade);

		let slideElements = React.Children.map(children, (child) => {
			if (child.props['data-film']) {
				const filmLink = child.props['data-film'];
				return React.cloneElement(child, {
					children: <video src={filmLink} autoPlay loop playsInline muted />,
				});
			}
			return child;
		});

		if (infinite && !disabled) {
			const startElements = React.Children.toArray(children).slice(-1 * infinite);
			const lastElements = React.Children.toArray(children).slice(0, infinite);

			slideElements = [
				...(React.Children.map(startElements, (startElement, i) => React.cloneElement(startElement, { key: `startElement_${i}` }))),
				...slideElements,
				...(React.Children.map(lastElements, (lastElement, i) => React.cloneElement(lastElement, { key: `lastElement_${i}` }))),
			];
		}

		const interactionEvents = {};
		if (!disabled) {
			interactionEvents.onTransitionEnd = this.handleTransitionEnd;

			if (interactionTypes.indexOf('touch') !== -1) {
				interactionEvents.onTouchStart = this.handleDragStart;
				interactionEvents.onTouchMove = this.handleDragMove;
				interactionEvents.onTouchEnd = this.handleDragEnd;
			}
			if (interactionTypes.indexOf('click') !== -1) {
				interactionEvents.onClick = (e) => this.handleClick(e, 1);
			}
			if (interactionTypes.indexOf('rightClick') !== -1) {
				interactionEvents.onContextMenu = (e) => this.handleClick(e, -1);
			}
		}

		return (
			<div className={sliderClasses.join(' ')} ref={this.slider}>
				{ showArrows && !disabled ? this.renderArrows() : null }
				{ showNav && !disabled ? this.renderNav() : null }
				<div className={s.slider_inner} ref={this.sliderInner} {...interactionEvents}>
					<div className={slidesClasses.join(' ')} style={slidesStyles}>
						{ slideElements }
					</div>
				</div>
				{ thumbs ? this.renderThumbs() : null }
			</div>
		);
	}
}

Slider.defaultProps = {
	selected: 0,
	startIndex: 0,
	infinite: 1, // 0, 1, 2
	autoplay: false, // false or delay in ms before slide
	thumbs: false, // false or true
	showArrows: true, // false or true
	showNav: true, // false or true
	adaption: false, // false or string
	transitionType: 'slide',
	transitionSpeed: 300,
	interactionTypes: ['touch'], // Array of strings - touch, click, rightClick
	screenMinWidthDisable: false, // false or integer - Disable slider under min width
	screenMaxWidthDisable: false, // false or integer - Disable slider over max width
};

export default hot(module)(Slider);
